import React, { useState } from 'react'
import {
  isNil,
  pathOr
} from 'ramda'
import ReactMarkdown from 'react-markdown'
// Local
import If from '../../common/if'
import {
  capitalizeFirstLetter,
  formatIcd10Code,
  removeEmptyValues
} from '../../v2/utils/utils'
import {
  EncounterConditionDisplayModified,
  User
} from '../../authenticated/types'
import { truncatedDescription } from '../../common/formatUtils'
import NLPExtractedLink from '../common/nlpExtractedLink'
import ConditionEvidence from '../common/conditionEvidence'
import { shouldRenderReskin2025 } from '../../../constant'
import ReveleerCoachingBox from '../../ReveleerCoachingBox'

type Props = {
  currentUser: User
  condition: EncounterConditionDisplayModified
  sources: (string | null)[]
}

const maxCharactersAllowed = 200

const truncate = truncatedDescription(maxCharactersAllowed)

const hasDescriptionFor =
  (condition: EncounterConditionDisplayModified) =>
    (x: string): boolean =>
      !!pathOr('', [ x ], condition).length

function Description(props: Props): JSX.Element {
  const { currentUser, condition, sources } = props

  const sourcesCapitalized = sources.map(capitalizeFirstLetter)

  const has = hasDescriptionFor(condition)

  const icd10CodeJsonToString: string = isNil(condition.icd10CodeJson)
    ? ''
    : condition.icd10CodeJson.map(formatIcd10Code).join(', ')

  const description = removeEmptyValues([
    condition.content,
    icd10CodeJsonToString
  ]) as string[]

  const shouldShowFullDescription =
    description.toString().length <= maxCharactersAllowed

  const [ shouldShowFullDescriptionState, setShouldShowFullDescriptionState ] =
    useState<boolean>(shouldShowFullDescription)

  return (
    <div className="curation-ui-description">
      <If isTrue={ shouldShowFullDescriptionState }>
        <If isTrue={ has('content') }>
          <ReactMarkdown>{ condition.content || '' }</ReactMarkdown>
          <ConditionEvidence
            condition={ condition }
            currentUser={ currentUser }
          />
          <NLPExtractedLink condition={ condition } currentUser={ currentUser } />
        </If>
        <If
          isTrue={ condition.isKnownAddressed && !!icd10CodeJsonToString.length }
        >
          <p>{ icd10CodeJsonToString }</p>
        </If>
      </If>
      { /* More Button */ }
      <If isTrue={ !shouldShowFullDescriptionState }>
        <span><ReactMarkdown>{ `${truncate(description[0])}...` }</ReactMarkdown></span>
        <span
          className="curation-ui-description-toggle"
          onClick={ () => setShouldShowFullDescriptionState(true) }
        >
          More
        </span>
      </If>
      { /* Less Button */ }
      <If isTrue={ !shouldShowFullDescription && shouldShowFullDescriptionState }>
        <span
          className="curation-ui-description-toggle"
          onClick={ () => setShouldShowFullDescriptionState(false) }
        >
          Less
        </span>
      </If>
      <If isTrue={ !!sources.length }>
        <If isTrue={ !shouldRenderReskin2025 }>
          <div className="source-container">
            <div className="source">
              <span className="bold">
                { `Source${sources.length > 1 ? 's' : ''}` }:{ ' ' }
              </span>
              <span>{ sourcesCapitalized.join('; ').replace('Source: ', '') }</span>
            </div>
          </div>
        </If>
        <If isTrue={ shouldRenderReskin2025 }>
          <ReveleerCoachingBox
            coachingBoxType="infoPositive"
            isShowing
            message={
              <>
                <span className="bold">
                  { `Source${sources.length > 1 ? 's' : ''}` }:{ ' ' }
                </span>
                <span>{ sourcesCapitalized.join('; ').replace('Source: ', '') }</span>
              </>
            }
          />
        </If>
      </If>
    </div>
  )
}

export default Description
